import { useState } from "react";
import { ArrowRight, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import productImg from "@/assets/product-img.png";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try { await login(email, password); } catch (err) { setError(errMsg(err, "Sign in failed")); } finally { setBusy(false); }
  };

  return (
    <div className="h-full grid grid-cols-1 lg:grid-cols-[60fr_40fr]" data-testid="login-page">
      <section className="relative bg-kl-black text-white overflow-hidden hidden lg:flex flex-col justify-between p-12 kl-grain kl-sprinkle-bg">
        {/* ambient light behind the product shot — a blurred solid, not a gradient fill */}
        <div className="absolute right-4 xl:right-10 top-1/2 -translate-y-1/2 w-[220px] h-[220px] bg-kl-green/10 blur-[70px] rounded-full pointer-events-none" />
        <img
          src={productImg}
          alt="KyraLink smart energy meter"
          className="absolute right-10 xl:right-16 top-1/2 -translate-y-1/2 w-[130px] xl:w-[150px] drop-shadow-[0_16px_28px_rgba(0,0,0,0.55)] pointer-events-none select-none"
        />

        <div className="relative flex items-center justify-between">
          <span className="font-display font-semibold tracking-[0.35em] text-sm">KYRALINK</span>
          <span className="kl-label-dark">Energy Intelligence Platform</span>
        </div>
        <div className="relative max-w-lg">
          <p className="kl-label-dark mb-6">/ sys.terminal — link established</p>
          <h1 className="font-display font-light text-5xl xl:text-6xl leading-[1.05] tracking-tight">
            Measure the load.<br />
            <span className="font-semibold">Size the sun.</span>
          </h1>
          <p className="mt-8 text-kl-muted leading-relaxed">
            Remote current and power monitoring for client homes and offices via SIM-based IoT nodes. Two weeks of data becomes a precise solar array, inverter and battery recommendation.
          </p>
        </div>
        <div className="relative grid grid-cols-3 gap-px bg-kl-line border border-kl-line max-w-xl">
          {[["ESP32", "SD-card + SIM uplink"], ["1 min", "sampling interval"], ["kWp", "auto sizing engine"]].map(([a, b]) => (
            <div key={a} className="bg-kl-black p-4">
              <div className="font-mono text-lg">{a}</div>
              <div className="kl-label-dark mt-1">{b}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white flex items-center">
        <form onSubmit={submit} className="w-full max-w-sm mx-auto px-8 lg:px-0 lg:ml-[18%] animate-rise" data-testid="login-form">
          <span className="font-display font-semibold tracking-[0.35em] text-sm lg:hidden">KYRALINK</span>
          <p className="kl-label mt-10 lg:mt-0">Operator access</p>
          <h2 className="font-display text-3xl font-light mt-2 mb-12">Sign in</h2>

          <label className="block mb-8">
            <span className="kl-label">Email</span>
            <input data-testid="login-email-input" type="email" required autoComplete="username" className="kl-input mt-1" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="operator@kyralink.io" />
          </label>
          <label className="block mb-10">
            <span className="kl-label">Password</span>
            <div className="relative mt-1">
              <input data-testid="login-password-input" type={showPassword ? "text" : "password"} required autoComplete="current-password" className="kl-input pr-8" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••••" />
              <button type="button" onClick={() => setShowPassword((v) => !v)} data-testid="login-password-toggle" tabIndex={-1} aria-label={showPassword ? "Hide password" : "Show password"} className="absolute right-0 top-1/2 -translate-y-1/2 text-kl-mutedLight hover:text-kl-black transition-colors duration-150">
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </label>

          {error && <p data-testid="login-error" className="mb-6 text-sm text-kl-inactive font-mono">{error}</p>}

          <button data-testid="login-submit-button" disabled={busy} className="kl-btn-primary w-full h-12 group">
            {busy ? "Authenticating…" : "Continue"}
            <ArrowRight size={16} className="transition-transform duration-150 group-hover:translate-x-1" />
          </button>
        </form>
      </section>
    </div>
  );
}
